import type { ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { ConnectQueryKeys } from '~/connect/api';

const COMMON_OPTIONS = {
  refetchOnWindowFocus: false,
  gcTime: Infinity,
  staleTime: Infinity,
  enabled: false,
};

type Options = {
  getLegalNotices?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<string>>
  >;
  getTermsOfUse?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<string>>
  >;
  getUseOfCookies?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<string>>
  >;
  getConfidentialityAgreements?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<string>>
  >;
};

export const useManageLegalPages = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi } = useApi();

  const getLegalNoticesQuery = useQuery({
    queryKey: ConnectQueryKeys.legalNotices.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<string>>(
        `/tenants/api/wtb/tenants/${tenantCode}/legal-pages/legal-notices`,
      ),
    ...COMMON_OPTIONS,
    ...options.getLegalNotices,
  });

  const getTermsOfUse = useQuery({
    queryKey: ConnectQueryKeys.termsOfUse.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<string>>(
        `/tenants/api/wtb/tenants/${tenantCode}/legal-pages/terms-of-use`,
      ),
    ...COMMON_OPTIONS,
    ...options.getTermsOfUse,
  });

  const getUseOfCookies = useQuery({
    queryKey: ConnectQueryKeys.useOfCookies.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<string>>(
        `/tenants/api/wtb/tenants/${tenantCode}/legal-pages/use-of-cookies`,
      ),
    ...COMMON_OPTIONS,
    ...options.getUseOfCookies,
  });

  const getConfidentialityAgreements = useQuery({
    queryKey: ConnectQueryKeys.confidentialityAgreements.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<string>>(
        `/tenants/api/wtb/tenants/${tenantCode}/legal-pages/confidentiality-agreements`,
      ),
    ...COMMON_OPTIONS,
    ...options.getConfidentialityAgreements,
  });

  return restrictApi(options, {
    getLegalNotices: getLegalNoticesQuery,
    getTermsOfUse: getTermsOfUse,
    getUseOfCookies: getUseOfCookies,
    getConfidentialityAgreements: getConfidentialityAgreements,
  });
};
